import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Eyebrow } from "../components/Primitives";
import GoogleButton, { startGoogleAuth } from "../account/GoogleButton";
import { useAccount } from "../account/AccountContext";
import { formatAuthError } from "../lib/accountsApi";
import AppShell from "../components/AppShell";
import PasswordInput from "../components/PasswordInput";
import SignupConsent, { isUnder18, UNDERAGE_MESSAGE } from "../components/SignupConsent";
import LocationInput from "../components/LocationInput";
import { api } from "../lib/api";
import { getSessionId } from "../lib/session";

export default function SignUp() {
    const { register, account } = useAccount();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [dob, setDob] = useState("");
    const [location, setLocation] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (account) {
        setTimeout(() => navigate("/"), 0);
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const pwOk = password.length >= 8;
    const consentOk = agreed && dob && !isUnder18(dob);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!emailOk) { setError("Please enter a valid email."); return; }
        if (!location) { setError("Please pick your city from the suggestions."); return; }
        if (!pwOk) { setError("Password must be at least 8 characters."); return; }
        if (isUnder18(dob)) { setError(UNDERAGE_MESSAGE); return; }
        setSubmitting(true);
        setError(null);
        try {
            const sid = getSessionId();
            await api.createUser(sid, null).catch(() => {});
            await api.saveLocation(sid, location).catch(() => {});
            await register(email.trim(), password, { date_of_birth: dob, accepted_terms: agreed });
            toast.success("Welcome to Ever After");
            navigate("/");
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                setError("This email already has an account. Sign in instead.");
            } else if (status === 403 && typeof detail === "string") {
                setError(detail);
            } else {
                setError(formatAuthError(detail));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col px-7 py-10" data-testid="signup-screen">
                <Eyebrow>New here</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-tight">
                    Create <span className="italic">your</span> account.
                </h1>
                <p className="mt-3 font-display italic text-base text-ink-muted leading-snug">
                    Save your results, sign in from any device, and pick up where you left off.
                </p>

                <div className="mt-6">
                    <GoogleButton onClick={() => startGoogleAuth("/auth/callback")} />
                </div>

                <div className="my-5 flex items-center gap-3">
                    <div className="flex-1 h-px bg-burgundy/15" />
                    <span className="text-[10px] tracking-[0.32em] uppercase text-ink-light">or with email</span>
                    <div className="flex-1 h-px bg-burgundy/15" />
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <Field label="Email">
                        <input
                            data-testid="signup-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={e => { setEmail(e.target.value); setError(null); }}
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                        />
                    </Field>
                    <Field label="Password (8+ chars)">
                        <PasswordInput
                            testId="signup-password"
                            autoComplete="new-password"
                            value={password}
                            onChange={e => { setPassword(e.target.value); setError(null); }}
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                        />
                    </Field>

                    <Field label="Where are you based?">
                        <LocationInput value={location} onChange={(v) => { setLocation(v); setError(null); }} idPrefix="signup-location" />
                    </Field>

                    <SignupConsent dob={dob} setDob={(v) => { setDob(v); setError(null); }} agreed={agreed} setAgreed={setAgreed} idPrefix="signup-page" />

                    {error && (
                        <div data-testid="signup-error" className="text-xs text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">
                            {error}
                        </div>
                    )}
                    <Btn
                        data-testid="signup-submit-btn"
                        type="submit"
                        disabled={submitting || !emailOk || !pwOk || !consentOk || !location}
                        className={(submitting || !emailOk || !pwOk || !consentOk || !location) ? "opacity-60" : ""}
                    >
                        {submitting ? "Creating account…" : "Create account"}
                    </Btn>
                </form>

                <div className="mt-6 flex items-center justify-between text-[11px] tracking-[0.24em] uppercase">
                    <Link to="/login" data-testid="signup-login-link" className="text-burgundy/80 underline underline-offset-4">
                        Already a member? Sign in
                    </Link>
                    <Link to="/" data-testid="signup-back-home" className="text-ink-muted">
                        ← Take the assessment
                    </Link>
                </div>
            </div>
        </AppShell>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">{label}</div>
            {children}
        </label>
    );
}
